import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Package, User, MapPin, Mail, Phone, Truck } from 'lucide-react';

const CARRIERS = [
  { value: 'usps', label: 'USPS', url: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=' },
  { value: 'ups', label: 'UPS', url: 'https://www.ups.com/track?tracknum=' },
  { value: 'fedex', label: 'FedEx', url: 'https://www.fedex.com/fedextrack/?trknbr=' },
  { value: 'dhl', label: 'DHL', url: 'https://www.dhl.com/us-en/home/tracking.html?tracking-id=' },
  { value: 'other', label: 'Other', url: '' },
];

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/orders', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load orders.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus, trackingNumber = null, trackingCarrier = null) => {
    try {
      const token = localStorage.getItem('adminToken');
      let url = `/api/admin/orders/${orderId}/status?status=${newStatus}`;
      if (trackingNumber) url += `&tracking_number=${encodeURIComponent(trackingNumber)}`;
      if (trackingCarrier) url += `&tracking_carrier=${encodeURIComponent(trackingCarrier)}`;
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to update');

      toast({
        title: "Success!",
        description: "Order updated.",
      });
      
      fetchOrders();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update order.",
        variant: "destructive",
      });
    }
  };

  const handleSaveTracking = (orderId, currentStatus) => {
    const input = trackingInputs[orderId] || {};
    const trackingNumber = input.number || '';
    const trackingCarrier = input.carrier || 'usps';
    updateOrderStatus(orderId, currentStatus, trackingNumber, trackingCarrier);
    setTrackingInputs(prev => ({ ...prev, [orderId]: undefined }));
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      shipped: '#8b5cf6',
      delivered: '#22c55e',
      cancelled: '#dc2626'
    };
    return colors[status] || '#736c64';
  };

  if (loading) {
    return <div className="text-center py-8">Loading orders...</div>;
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="h-16 w-16 mx-auto mb-4" style={{ color: '#736c64' }} />
        <h3 className="text-xl font-semibold mb-2" style={{ color: '#4a2c1a' }}>No orders yet</h3>
        <p style={{ color: '#736c64' }}>Orders will appear here when customers make purchases.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold mb-6" style={{ color: '#4a2c1a' }}>
        Orders Management
      </h2>

      <div className="space-y-4">
        {orders.map((order) => (
          <div key={order.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold" style={{ color: '#4a2c1a' }}>
                    Order #{order.orderNumber}
                  </h3>
                  <p className="text-sm" style={{ color: '#736c64' }}>
                    {new Date(order.createdAt).toLocaleDateString()} at {new Date(order.createdAt).toLocaleTimeString()}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <span
                    className="px-4 py-2 rounded-full text-sm font-semibold text-white"
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                  <Button
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    variant="outline"
                  >
                    {expandedOrder === order.id ? 'Hide Details' : 'View Details'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>Customer</p>
                  <p className="text-sm" style={{ color: '#736c64' }}>
                    {order.customerInfo.firstName} {order.customerInfo.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>Total</p>
                  <p className="text-xl font-bold" style={{ color: '#FF8C19' }}>
                    ${order.total.toFixed(2)}
                  </p>
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: '#e5d5c0' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#4a2c1a' }}>
                        <User className="h-4 w-4 mr-2" />
                        Customer Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p className="flex items-center" style={{ color: '#736c64' }}>
                          <Mail className="h-4 w-4 mr-2" />
                          {order.customerInfo.email}
                        </p>
                        <p className="flex items-center" style={{ color: '#736c64' }}>
                          <Phone className="h-4 w-4 mr-2" />
                          {order.customerInfo.phone}
                        </p>
                        <p className="flex items-start" style={{ color: '#736c64' }}>
                          <MapPin className="h-4 w-4 mr-2 mt-1" />
                          <span>
                            {order.customerInfo.address}<br />
                            {order.customerInfo.city}, {order.customerInfo.state} {order.customerInfo.zipCode}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#4a2c1a' }}>
                        <Package className="h-4 w-4 mr-2" />
                        Order Items
                      </h4>
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span style={{ color: '#736c64' }}>
                              {item.quantity}x {item.name} ({item.size})
                              {item.variant && (
                                <span className="block text-xs mt-0.5" style={{ color: '#FF8C19' }}>
                                  {item.variant}
                                </span>
                              )}
                            </span>
                            <span style={{ color: '#4a2c1a' }} className="font-semibold">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        ))}
                        <div className="pt-2 mt-2 border-t" style={{ borderColor: '#e5d5c0' }}>
                          <div className="flex justify-between text-sm">
                            <span style={{ color: '#736c64' }}>Subtotal:</span>
                            <span style={{ color: '#4a2c1a' }}>${order.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span style={{ color: '#736c64' }}>Shipping:</span>
                            <span style={{ color: '#4a2c1a' }}>${order.shipping.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span style={{ color: '#736c64' }}>Tax:</span>
                            <span style={{ color: '#4a2c1a' }}>${order.tax.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-base font-bold mt-2">
                            <span style={{ color: '#4a2c1a' }}>Total:</span>
                            <span style={{ color: '#FF8C19' }}>${order.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tracking Info Display */}
                  {order.trackingNumber && (
                    <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                      <div className="flex items-center mb-1">
                        <Truck className="h-4 w-4 mr-2" style={{ color: '#22c55e' }} />
                        <span className="text-sm font-semibold" style={{ color: '#166534' }}>Tracking Info</span>
                      </div>
                      <p className="text-sm" style={{ color: '#4a2c1a' }}>
                        <strong>{(order.trackingCarrier || 'carrier').toUpperCase()}:</strong> {order.trackingNumber}
                      </p>
                    </div>
                  )}

                  {/* Tracking Number Input */}
                  <div className="mb-4 p-4 rounded-lg border" style={{ borderColor: '#e5d5c0' }}>
                    <label className="block text-sm font-semibold mb-2 flex items-center" style={{ color: '#4a2c1a' }}>
                      <Truck className="h-4 w-4 mr-2" />
                      Tracking Number
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Select
                        value={(trackingInputs[order.id]?.carrier) || order.trackingCarrier || 'usps'}
                        onValueChange={(val) => setTrackingInputs(prev => ({
                          ...prev,
                          [order.id]: { ...prev[order.id], carrier: val }
                        }))}
                      >
                        <SelectTrigger className="w-full sm:w-36">
                          <SelectValue placeholder="Carrier" />
                        </SelectTrigger>
                        <SelectContent>
                          {CARRIERS.map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        data-testid={`tracking-input-${order.id}`}
                        placeholder={order.trackingNumber || "Enter tracking number"}
                        defaultValue={order.trackingNumber || ''}
                        onChange={(e) => setTrackingInputs(prev => ({
                          ...prev,
                          [order.id]: { ...prev[order.id], number: e.target.value }
                        }))}
                        className="flex-1"
                      />
                      <Button
                        data-testid={`save-tracking-btn-${order.id}`}
                        onClick={() => handleSaveTracking(order.id, order.status)}
                        className="text-white font-semibold"
                        style={{ backgroundColor: '#22c55e' }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#4a2c1a' }}>
                      Update Order Status
                    </label>
                    <Select value={order.status} onValueChange={(value) => updateOrderStatus(order.id, value)}>
                      <SelectTrigger className="w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrdersTab;
